import { useState, useEffect, useCallback } from "react";

interface GalleryImage {
  url: string;
  filename: string;
}

export default function GallerySection() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    fetch("https://kcontents.eemo.co.kr/api/gallery_list.php")
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.images.length > 0) {
          setImages(data.images);
        }
      })
      .catch((err) => {
        console.log("갤러리 로딩 실패", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    document.body.style.overflow = "";
  }, []);

  const showPrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + images.length) % images.length);
  }, [lightboxIndex, images.length]);

  const showNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % images.length);
  }, [lightboxIndex, images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  return (
    <section id="gallery" className="relative w-full py-16 md:py-24" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="animate-on-scroll text-center mb-10 md:mb-14">
            <p className="text-sky-500 text-sm font-semibold tracking-wider uppercase mb-3">
              Photo Gallery
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-navy-900">
              사진 갤러리
            </h2>
            <p className="text-navy-500 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              한국문화체육진흥원의 다양한 활동과 행사 사진을 만나보세요.
            </p>
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex gap-2 items-center text-navy-400">
                <i className="ri-loader-4-line text-2xl animate-spin" />
                <span className="text-sm">사진을 불러오는 중...</span>
              </div>
            </div>
          ) : images.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center text-navy-400">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-image-line text-5xl text-navy-200" />
                </div>
                <p className="text-sm">등록된 사진이 없습니다.</p>
              </div>
            </div>
          ) : (
            <div
              id="gallery-grid"
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
              style={{ gap: "10px" }}
            >
              {images.map((img, index) => (
                <div
                  key={index}
                  className="relative overflow-hidden cursor-pointer group"
                  style={{ paddingBottom: "100%", height: 0 }}
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={img.url}
                    alt={img.filename}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 group-hover:brightness-90"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-zoom-in-line text-white text-2xl drop-shadow" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white hover:text-sky-300 transition-colors z-10"
            onClick={closeLightbox}
            aria-label="닫기"
          >
            <i className="ri-close-line text-3xl" />
          </button>

          {/* Prev Arrow */}
          {images.length > 1 && (
            <button
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white hover:text-sky-300 transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              aria-label="이전"
            >
              <i className="ri-arrow-left-s-line text-4xl" />
            </button>
          )}

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex].url}
              alt={images[lightboxIndex].filename}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>

          {/* Next Arrow */}
          {images.length > 1 && (
            <button
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white hover:text-sky-300 transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              aria-label="다음"
            >
              <i className="ri-arrow-right-s-line text-4xl" />
            </button>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {lightboxIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
}